import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";
import type { Category, Topic, Video } from "@/types";

export const allVideos: Video[] = videos;
export const allCategories: Category[] = categories;
export const allTopics: Topic[] = topics;

export function getVideoById(id: string | undefined): Video | undefined {
  if (!id) return undefined;
  return videos.find((v) => v.id === id);
}

export function getCategoryById(id: string | undefined): Category | undefined {
  if (!id) return undefined;
  return categories.find((c) => c.id === id);
}

export function getTopicById(id: string | undefined): Topic | undefined {
  if (!id) return undefined;
  return topics.find((t) => t.id === id);
}

export function getVideosByCategory(categoryId: string): Video[] {
  return videos.filter((v) => v.categoryId === categoryId);
}

export function getVideosByIds(ids: string[]): Video[] {
  return ids
    .map((id) => videos.find((v) => v.id === id))
    .filter((v): v is Video => Boolean(v));
}

export function getFeaturedVideos(limit = 8): Video[] {
  return [...videos]
    .sort((a, b) => Number(b.rating) - Number(a.rating))
    .slice(0, limit);
}

export function getLatestVideos(limit = 12): Video[] {
  return [...videos]
    .sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime())
    .slice(0, limit);
}

export function searchVideos(keyword: string): Video[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return videos.filter((v) => {
    const haystack = [v.title, v.type, v.region, v.description, ...v.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelatedVideos(video: Video, limit = 6): Video[] {
  const sameCategory = videos.filter(
    (v) => v.categoryId === video.categoryId && v.id !== video.id,
  );
  if (sameCategory.length >= limit) return sameCategory.slice(0, limit);
  const others = videos.filter(
    (v) => v.categoryId !== video.categoryId && v.id !== video.id,
  );
  return [...sameCategory, ...others].slice(0, limit);
}

export const regions: string[] = ["全部", "中国大陆", "中国香港", "美国", "日本", "英国"];

export const years: string[] = ["全部", "2024", "2023", "2022"];

export function getCategoryTypes(categoryId: string): string[] {
  const types = getVideosByCategory(categoryId).map((v) => v.type);
  return ["全部", ...Array.from(new Set(types))];
}

export function getAllTags(categoryId: string): string[] {
  const tags = getVideosByCategory(categoryId).flatMap((v) => v.tags);
  return Array.from(new Set(tags)).slice(0, 10);
}